import React, { useEffect, useRef, useState } from 'react';
import { Truck, Clock, Shield, Headphones, MapPin, Users } from 'lucide-react';

const Services: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: <Truck className="text-blue-500" size={48} />,
      title: "Entrega Rápida",
      description: "Entregamos seus produtos frescos em até 24 horas em toda a região metropolitana",
      features: ["Entrega em 24h", "Rastreamento em tempo real", "Embalagem refrigerada"]
    },
    {
      icon: <Clock className="text-green-500" size={48} />,
      title: "Atendimento",
      description: "Nossa equipe está disponível para atender suas necessidades",
      features: ["Suporte 7 dias da semana", "Atendimento especializado", "Resposta rápida"]
    },
    {
      icon: <Shield className="text-purple-500" size={48} />,
      title: "Garantia de Qualidade",
      description: "Todos os produtos passam por rigoroso controle de qualidade antes da entrega",
      features: ["Controle rigoroso", "Produtos frescos", "Satisfação garantida"]
    },
    {
      icon: <Headphones className="text-orange-500" size={48} />,
      title: "Consultoria Especializada",
      description: "Nossa equipe oferece consultoria personalizada para escolha dos melhores produtos",
      features: ["Consultoria gratuita", "Recomendações personalizadas", "Suporte técnico"]
    },
    {
      icon: <MapPin className="text-red-500" size={48} />,
      title: "Cobertura Regional",
      description: "Atendemos o interior de São Paulo (região de Sorocaba), cidade de São Paulo e toda a Baixada Santista, sempre com qualidade e pontualidade.",
      features: [
        "Interior de SP (região de Sorocaba)",
        "Cidade de São Paulo",
        "Baixada Santista"
      ]
    },
    {
      icon: <Users className="text-indigo-500" size={48} />,
      title: "Atendimento Personalizado",
      description: "Cada cliente recebe um atendimento único, adaptado às suas necessidades específicas",
      features: ["Atendimento individual", "Soluções customizadas", "Relacionamento duradouro"]
    }
  ];

  return (
    <section ref={sectionRef} id="services" className="section-padding bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container-custom">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'scroll-reveal visible' : 'scroll-reveal'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-primary-800 mb-4">
            Nossos <span className="text-gradient">Serviços</span>
          </h2>
          <p className="text-xl text-primary-600 max-w-3xl mx-auto">
            Oferecemos uma gama completa de serviços para garantir que você receba os melhores produtos com a máxima conveniência
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <div 
              key={index}
              className={`bg-white rounded-3xl p-8 shadow-lg card-hover transition-all duration-1000 delay-${(index + 1) * 100} ${isVisible ? 'scroll-scale visible' : 'scroll-scale'}`}
            >
              <div className="flex items-center mb-6">
                <div className="p-4 bg-gray-50 rounded-2xl mr-4">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-primary-800">{service.title}</h3>
              </div>
              
              <p className="text-primary-600 mb-6 leading-relaxed">
                {service.description}
              </p>
              
              <ul className="space-y-2">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center text-sm text-primary-600">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mr-3"></div>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Process Section */}
        <div className={`mb-16 transition-all duration-1000 delay-400 ${isVisible ? 'scroll-reveal visible' : 'scroll-reveal'}`}>
          <h3 className="text-3xl font-bold text-center text-primary-800 mb-12">
            Como Funciona Nosso Processo
          </h3>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary-600 font-bold text-xl">1</span>
              </div>
              <h4 className="font-semibold text-primary-800 mb-2">Seleção</h4>
              <p className="text-primary-600 text-sm">Escolhemos os melhores produtos direto dos produtores</p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary-600 font-bold text-xl">2</span>
              </div>
              <h4 className="font-semibold text-primary-800 mb-2">Controle</h4>
              <p className="text-primary-600 text-sm">Rigoroso controle de qualidade em todas as etapas</p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary-600 font-bold text-xl">3</span>
              </div>
              <h4 className="font-semibold text-primary-800 mb-2">Embalagem</h4>
              <p className="text-primary-600 text-sm">Embalagem cuidadosa para preservar a qualidade</p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary-600 font-bold text-xl">4</span>
              </div>
              <h4 className="font-semibold text-primary-800 mb-2">Entrega</h4>
              <p className="text-primary-600 text-sm">Entrega rápida e segura diretamente ao cliente</p>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className={`bg-white rounded-3xl p-8 md:p-12 shadow-lg transition-all duration-1000 delay-600 ${isVisible ? 'scroll-reveal visible' : 'scroll-reveal'}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-primary-600 mb-2">99%</div>
              <div className="text-primary-600 font-medium">Satisfação do Cliente</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary-600 mb-2">24h</div>
              <div className="text-primary-600 font-medium">Tempo de Entrega</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary-600 mb-2">20+</div>
              <div className="text-primary-600 font-medium">Regiões Atendidas</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary-600 mb-2">70+</div>
              <div className="text-primary-600 font-medium">Anos de Experiência</div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className={`text-center mt-16 transition-all duration-1000 delay-800 ${isVisible ? 'scroll-reveal visible' : 'scroll-reveal'}`}>
          <h3 className="text-3xl font-bold text-primary-800 mb-6">
            Pronto para Experimentar Nossos Serviços?
          </h3>
          <p className="text-xl text-primary-600 mb-8 max-w-2xl mx-auto">
            Entre em contato conosco e descubra como podemos atender suas necessidades com excelência
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://wa.me/551532415413" 
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Solicitar Orçamento
            </a>
            <button 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-outline"
            >
              Fale Conosco
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
